import { J2Caution, K1Information } from '@e3dc-react/icons'
import { Card, SxProps, Typography, TypographyProps } from '@mui/material'
import React from 'react'

type MessageType = 'info' | 'warning' | 'error'

export interface Props {
  messageType: MessageType
  text: string
  sx?: SxProps
  iconColor?: string
  textVariant?: TypographyProps['variant']
}

/**
 * Information Box is a  component that is displayed inside setting boxes or cards.
 * It is used to inform users about important information, warnings, or errors.
 */
const InformationBox: React.FC<Props> = ({ text, messageType, sx, iconColor, textVariant = 'body2' }) => {
  const defaultBackgroundColors = {
    info: 'shades.shade04',
    warning: 'warning.light',
    error: 'error.light',
  }

  const defaultIconColors = {
    info: 'grey.grey00',
    warning: 'warning.main',
    error: 'error.main',
  }

  const defaultBackgroundColor = defaultBackgroundColors[messageType]
  const iconColorToUse = iconColor ?? defaultIconColors[messageType]

  const iconComponent = getIconComponent(messageType, iconColorToUse)

  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        gap: 2,
        padding: 2,
        width: '100%',
        backgroundColor: defaultBackgroundColor,
        boxShadow: 'none',
        border: messageType === 'info' ? '1px solid' : 'none',
        borderColor: messageType === 'info' ? 'shades.shade03' : undefined,
        ...sx,
      }}
    >
      {iconComponent}
      <Typography variant={textVariant}>{text}</Typography>
    </Card>
  )
}

export default InformationBox

const getIconComponent = (messageType: MessageType, iconColor: string): JSX.Element | null => {
  switch (messageType) {
    case 'info':
      return <K1Information sx={{ color: iconColor }} />
    case 'warning':
      return <J2Caution sx={{ color: iconColor }} />
    case 'error':
      return <J2Caution sx={{ color: iconColor }} />
    default:
      return null
  }
}
